import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Card, Form, Button, Table, Badge, InputGroup, Modal } from "react-bootstrap";
import { displayUserPhone } from "../../utils/staffPhone";

export default function BonusLinks() {
  const baseUrl =
    process.env.NODE_ENV === "development"
      ? process.env.REACT_APP_BACKEND_LOCAL_API
      : process.env.REACT_APP_BACKEND_LIVE_API;

  const headers = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  });

  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bonusAmount, setBonusAmount] = useState("");
  const [expiresInHours, setExpiresInHours] = useState("24");
  const [note, setNote] = useState("");
  const [maxClaims, setMaxClaims] = useState("");
  const [randomPrize, setRandomPrize] = useState(false);
  const [creating, setCreating] = useState(false);
  const [lastCreatedUrl, setLastCreatedUrl] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [allClaims, setAllClaims] = useState([]);
  const [allClaimsLoading, setAllClaimsLoading] = useState(true);
  const [claimModal, setClaimModal] = useState({
    open: false,
    bonusAmount: null,
    rows: [],
    loading: false,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${baseUrl}admin/bonus-links`, { headers: headers() });
      setLinks(res.data.links || []);
    } catch (e) {
      console.error(e);
      setLinks([]);
    } finally {
      setLoading(false);
    }
  }, [baseUrl]);

  const loadAllClaims = useCallback(async () => {
    setAllClaimsLoading(true);
    try {
      const res = await axios.get(`${baseUrl}admin/bonus-links/claims/all?limit=2000`, {
        headers: headers(),
      });
      setAllClaims(res.data.claims || []);
    } catch (e) {
      console.error(e);
      setAllClaims([]);
    } finally {
      setAllClaimsLoading(false);
    }
  }, [baseUrl]);

  useEffect(() => {
    load();
    loadAllClaims();
  }, [load, loadAllClaims]);

  const createLink = async (e) => {
    e.preventDefault();
    const amt = Number(bonusAmount);
    if (!Number.isFinite(amt) || amt < 1 || amt > 500) {
      alert("Enter a valid bonus amount between ₹1 and ₹500.");
      return;
    }
    setCreating(true);
    setLastCreatedUrl("");
    try {
      const body = {
        bonusAmount: amt,
        randomPrize,
        expiresInHours: Number(expiresInHours) || 24,
        note: note.trim(),
      };
      const mc = maxClaims.trim();
      if (mc !== "") body.maxClaims = Number(mc);
      const res = await axios.post(`${baseUrl}admin/bonus-links`, body, {
        headers: { ...headers(), "Content-Type": "application/json" },
      });
      setLastCreatedUrl(res.data.claimUrl || "");
      setBonusAmount("");
      setNote("");
      await load();
      await loadAllClaims();
    } catch (err) {
      alert(err.response?.data?.message || err.message || "Failed");
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (id, active) => {
    try {
      await axios.patch(
        `${baseUrl}admin/bonus-links/${id}`,
        { active: !active },
        { headers: { ...headers(), "Content-Type": "application/json" } }
      );
      await load();
    } catch (err) {
      alert(err.response?.data?.message || "Failed");
    }
  };

  const deleteLink = async (id) => {
    if (
      !window.confirm(
        "Permanently delete this bonus link and its claim records? Users will no longer be able to use the URL."
      )
    ) {
      return;
    }
    setDeletingId(id);
    try {
      await axios.delete(`${baseUrl}admin/bonus-links/${id}`, { headers: headers() });
      await load();
      await loadAllClaims();
    } catch (err) {
      alert(err.response?.data?.message || err.message || "Delete failed");
    } finally {
      setDeletingId(null);
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text).then(
      () => alert("Link copied."),
      () => alert(text)
    );
  };

  const openClaimsModal = async (linkId, bonusAmount) => {
    setClaimModal({ open: true, bonusAmount, rows: [], loading: true });
    try {
      const res = await axios.get(`${baseUrl}admin/bonus-links/${linkId}/claims`, { headers: headers() });
      setClaimModal({
        open: true,
        bonusAmount,
        rows: res.data.claims || [],
        loading: false,
      });
    } catch (err) {
      alert(err.response?.data?.message || err.message || "Failed to load claims");
      setClaimModal({ open: false, bonusAmount: null, rows: [], loading: false });
    }
  };

  const closeClaimsModal = () => {
    setClaimModal({ open: false, bonusAmount: null, rows: [], loading: false });
  };

  return (
    <div className="row">
      <div className="col-12 grid-margin">
        <Card className="mb-4">
          <Card.Body>
            <h4 className="card-title mb-2">Telegram / promo bonus links</h4>
            <p className="text-muted small mb-3">
              Shared links use <strong>https://ludoflix.com/bonus/…</strong> and expire after the hours you set
              (default <strong>24 hours</strong>). Users can claim only if they have at least <strong>₹100</strong>{" "}
              successful deposit in the current week (Mon–Sun UTC, same rule as spin). Each user can use a link once.
              <span className="d-block mt-2">
                <strong>Scratch prize:</strong> by default every player gets the <strong>exact ₹ amount</strong> you enter.
                Enable <em>Random prize</em> only if you want ₹1…max (smaller wins more likely).{" "}
                <strong>Agent accounts cannot claim.</strong>
              </span>
            </p>
            <Form onSubmit={createLink}>
              <Form.Row>
                <Form.Group className="col-md-3">
                  <Form.Label>Bonus amount (₹)</Form.Label>
                  <Form.Control
                    type="number"
                    min={1}
                    max={500}
                    value={bonusAmount}
                    onChange={(e) => setBonusAmount(e.target.value)}
                    placeholder="e.g. 100"
                    required
                  />
                  <Form.Text className="text-muted">
                    Max ₹500. Fixed: every user gets this exact amount on scratch.
                  </Form.Text>
                </Form.Group>
                <Form.Group className="col-md-3">
                  <Form.Label>Random prize</Form.Label>
                  <Form.Check
                    type="checkbox"
                    id="bonus-link-random-prize"
                    label="Random ₹1…max (not fixed)"
                    checked={randomPrize}
                    onChange={(e) => setRandomPrize(e.target.checked)}
                  />
                  <Form.Text className="text-muted">
                    Off = same as amount above for all users.
                  </Form.Text>
                </Form.Group>
                <Form.Group className="col-md-3">
                  <Form.Label>Valid for (hours)</Form.Label>
                  <Form.Control
                    type="number"
                    min={1}
                    max={1440}
                    value={expiresInHours}
                    onChange={(e) => setExpiresInHours(e.target.value)}
                  />
                  <Form.Text className="text-muted">Default 24 hours (link expires after that)</Form.Text>
                </Form.Group>
                <Form.Group className="col-md-3">
                  <Form.Label>Max claims (optional)</Form.Label>
                  <Form.Control
                    type="number"
                    min={1}
                    placeholder="Unlimited"
                    value={maxClaims}
                    onChange={(e) => setMaxClaims(e.target.value)}
                  />
                </Form.Group>
                <Form.Group className="col-md-3">
                  <Form.Label>Note (optional)</Form.Label>
                  <Form.Control value={note} onChange={(e) => setNote(e.target.value)} placeholder="Campaign name" />
                </Form.Group>
              </Form.Row>
              <Button type="submit" variant="primary" disabled={creating}>
                {creating ? "Creating…" : "Generate link"}
              </Button>
            </Form>
            {lastCreatedUrl && (
              <div className="mt-3 p-3 bg-light rounded border">
                <div className="small text-muted mb-1">New link (share on Telegram)</div>
                <InputGroup>
                  <Form.Control readOnly value={lastCreatedUrl} />
                  <InputGroup.Append>
                    <Button variant="outline-secondary" onClick={() => copy(lastCreatedUrl)}>
                      Copy
                    </Button>
                  </InputGroup.Append>
                </InputGroup>
              </div>
            )}
          </Card.Body>
        </Card>

        <Card>
          <Card.Body>
            <h5 className="mb-3">Recent links</h5>
            {loading ? (
              <p className="text-muted">Loading…</p>
            ) : (
              <Table responsive striped size="sm">
                <thead>
                  <tr>
                    <th>Amount</th>
                    <th>Expires</th>
                    <th>Claims</th>
                    <th>Status</th>
                    <th>Link</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {links.map((r) => (
                    <tr key={r._id}>
                      <td>
                        ₹{r.bonusAmount}
                        {r.randomPrize ? (
                          <Badge variant="info" className="ml-1">
                            random
                          </Badge>
                        ) : null}
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>{new Date(r.expiresAt).toLocaleString()}</td>
                      <td>
                        {r.claimsCount || 0}
                        {r.maxClaims != null ? ` / ${r.maxClaims}` : ""}
                      </td>
                      <td>
                        <Badge variant={r.active ? "success" : "secondary"}>{r.active ? "Active" : "Off"}</Badge>
                      </td>
                      <td style={{ maxWidth: 220, wordBreak: "break-all", fontSize: 12 }}>
                        {r.claimUrl}
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          className="mr-1 mb-1"
                          onClick={() => openClaimsModal(r._id, r.bonusAmount)}
                        >
                          Who claimed
                        </Button>
                        <Button size="sm" variant="outline-primary" className="mr-1 mb-1" onClick={() => copy(r.claimUrl)}>
                          Copy
                        </Button>
                        {r.active && (
                          <Button size="sm" variant="outline-danger" className="mr-1 mb-1" onClick={() => toggleActive(r._id, r.active)}>
                            Disable
                          </Button>
                        )}
                        {!r.active && (
                          <Button
                            size="sm"
                            variant="outline-success"
                            className="mr-1 mb-1"
                            onClick={() => toggleActive(r._id, r.active)}
                          >
                            Enable
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline-danger"
                          className="mb-1"
                          disabled={deletingId === r._id}
                          onClick={() => deleteLink(r._id)}
                        >
                          {deletingId === r._id ? "…" : "Delete"}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
            {!loading && links.length === 0 && <p className="text-muted mb-0">No links yet.</p>}
          </Card.Body>
        </Card>

        <Card className="mt-4">
          <Card.Body>
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
              <h5 className="mb-0">All claim history</h5>
              <Button size="sm" variant="outline-secondary" onClick={loadAllClaims} disabled={allClaimsLoading}>
                {allClaimsLoading ? "Refreshing…" : "Refresh"}
              </Button>
            </div>
            {allClaimsLoading ? (
              <p className="text-muted mb-0">Loading claim history…</p>
            ) : allClaims.length === 0 ? (
              <p className="text-muted mb-0">No claims yet.</p>
            ) : (
              <Table responsive striped size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>User</th>
                    <th>Phone</th>
                    <th>Amount</th>
                    <th>Link code</th>
                    <th>Amount</th>
                    <th>Claimed at</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {allClaims.map((c, idx) => (
                    <tr key={c._id}>
                      <td>{idx + 1}</td>
                      <td>{c.user?.Name || "—"}</td>
                      <td>{displayUserPhone(c.user?.Phone)}</td>
                      <td>₹{c.amount ?? "—"}</td>
                      <td className="text-monospace small">
                        {c.link?.code ? `${String(c.link.code).slice(0, 8)}…` : "—"}
                      </td>
                      <td>{c.link?.maxPrize != null ? `₹${c.link.maxPrize}` : "—"}</td>
                      <td>{c.claimedAt ? new Date(c.claimedAt).toLocaleString() : "—"}</td>
                      <td>
                        {c.user?._id && (
                          <Link className="btn btn-sm btn-outline-info" to={`/user/view_user/${c.user._id}`}>
                            Profile
                          </Link>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>

        <Modal show={claimModal.open} onHide={closeClaimsModal} size="lg" centered>
          <Modal.Header closeButton>
            <Modal.Title>
              Who claimed this link
              {claimModal.bonusAmount != null ? (
                <span className="text-muted font-weight-normal small ml-2">
                  (max ₹{claimModal.bonusAmount}; amount varies per user)
                </span>
              ) : null}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {claimModal.loading ? (
              <p className="text-muted mb-0">Loading…</p>
            ) : claimModal.rows.length === 0 ? (
              <p className="text-muted mb-0">No claims yet for this link.</p>
            ) : (
              <Table responsive striped size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>User</th>
                    <th>Phone</th>
                    <th>Amount</th>
                    <th>Claimed at</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {claimModal.rows.map((c, idx) => (
                    <tr key={c._id}>
                      <td>{idx + 1}</td>
                      <td>{c.user?.Name || "—"}</td>
                      <td>{displayUserPhone(c.user?.Phone)}</td>
                      <td>₹{c.amount ?? "—"}</td>
                      <td>{c.claimedAt ? new Date(c.claimedAt).toLocaleString() : "—"}</td>
                      <td>
                        {c.user?._id && (
                          <Link className="btn btn-sm btn-outline-info" to={`/user/view_user/${c.user._id}`}>
                            Profile
                          </Link>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Modal.Body>
        </Modal>
      </div>
    </div>
  );
}
